<template>
  <SlideIn
    :open="openFilters"
    :content-padding="false"
    heading="Filters"
    @close="openFilters = false"
  >
    <template #content>
      <div class="nosto-filters">
        <AccordionGroup>
          <Accordion v-if="showSort" title="Sort">
            <ul>
              <li v-for="option in sortOptions" :key="option.value">
                <button
                  class="list-filter-button"
                  :class="{
                    'list-filter-button--active':
                      option.value === activeSort.value,
                  }"
                  @click="handleSortChange(option)"
                >
                  {{ option.label }}
                </button>
              </li>
            </ul>
          </Accordion>
          <Accordion
            v-for="facet in renderableFacets"
            :key="facet.id || facet.field"
            :title="facet.name"
          >
            <!-- Price / stats facet -->
            <div v-if="isStatsFacet(facet)" class="nosto-filters__price">
              <form class="form">
                <label
                  v-for="(band, index) in priceBands(facet)"
                  :key="index"
                  class="field field--checkbox"
                >
                  <span class="field__label">{{ formatBand(band) }}</span>
                  <div class="input input--checkbox">
                    <input
                      :checked="isBandSelected(facet.field, band)"
                      type="checkbox"
                      class="input__element input__element--checkbox"
                      @change="togglePriceBand(facet.field, band)"
                    />
                    <span class="input__checkbox"></span>
                  </div>
                </label>
              </form>
            </div>

            <ColourSwatchOptions
              v-else-if="isColourFacet(facet)"
              :items="colourSwatchItems(facet)"
              @change="toggleColourSwatch(facet, $event)"
            />

            <!-- Terms facet (Category, Size, ...) -->
            <div v-else class="nosto-filters__terms">
              <form class="form">
                <label
                  v-for="value in facetValues(facet)"
                  :key="value.value"
                  class="field field--checkbox"
                >
                  <span class="field__label">
                    {{ value.value }} ({{ value.count }})
                  </span>
                  <div class="input input--checkbox">
                    <input
                      :checked="isTermSelected(facet.field, value.value)"
                      type="checkbox"
                      class="input__element input__element--checkbox"
                      @change="toggleTerm(facet.field, value.value)"
                    />
                    <span class="input__checkbox"></span>
                  </div>
                </label>
              </form>
            </div>
          </Accordion>
        </AccordionGroup>
      </div>
    </template>
    <template #footer>
      <div class="button-group">
        <Button theme="secondary" width="full" @click="handleResetClick">
          Clear All
        </Button>
        <Button width="full" @click="applyFilter">See Results</Button>
      </div>
    </template>
  </SlideIn>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import cloneDeep from "lodash-es/cloneDeep";
import { nostoFacetValueAllowlists } from "@/config";
import SlideIn from "@/components/SlideIn.vue";
import AccordionGroup from "@/components/AccordionGroup.vue";
import Accordion from "@/components/AccordionV2.vue";
import Button from "@/components/ButtonV3.vue";
import ColourSwatchOptions from "@/components/collection/filters/ColourSwatchOptions.vue";
import { isColourFilterName } from "@/helpers/filterColourSwatches";

const PRICE_BANDS = [
  { min: 0, max: 99.99 },
  { min: 100, max: 249.99 },
  { min: 250, max: 499.99 },
  { min: 500, max: null },
];

export default {
  name: "NostoSearchFilters",
  components: {
    SlideIn,
    AccordionGroup,
    Accordion,
    Button,
    ColourSwatchOptions,
  },
  props: {
    facets: {
      type: Array,
      default: () => [],
    },
    // Collection pages use a single "Filters & Sort" drawer on mobile, so
    // the sort options are rendered here too when enabled.
    showSort: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["filterChange"],
  data() {
    return {
      // { [field]: [value, ...] } for terms facets
      termSelections: {},
      // { [field]: [bandIndex, ...] } for stats/price facets
      priceSelections: {},
    };
  },
  computed: {
    ...mapState("collection", ["isFiltersOpen", "sortOptions"]),
    ...mapGetters("collection", ["activeSort"]),
    ...mapGetters("app", ["currencyCode"]),
    openFilters: {
      get() {
        return this.isFiltersOpen;
      },
      set(value) {
        this.setIsFiltersOpen(value);
      },
    },
    renderableFacets() {
      return (this.facets || []).filter((facet) => {
        if (this.isStatsFacet(facet)) {
          return facet.max != null && facet.max > 0;
        }
        return (this.facetValues(facet) || []).length > 0;
      });
    },
  },
  watch: {
    // Keep terms in sync with what Nosto reports as applied. Price has no
    // selected flag on stats facets, so it stays purely local.
    facets: {
      handler() {
        this.seedTermSelections();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions("collection", ["setIsFiltersOpen", "setSort"]),
    handleSortChange(option) {
      const value = option.value;
      const reverse = value.includes("_DESC");
      const strippedValue = value.replace("_ASC", "").replace("_DESC", "");

      this.setSort({ value: strippedValue, reverse });
    },
    isStatsFacet(facet) {
      return facet.type === "stats" || facet.__typename === "SearchStatsFacet";
    },
    isColourFacet(facet) {
      return isColourFilterName(facet?.name);
    },
    colourSwatchItems(facet) {
      return [...this.facetValues(facet)]
        .sort((a, b) => a.value.localeCompare(b.value))
        .map((value) => ({
          id: value.value,
          label: value.value,
          count: value.count,
          selected: this.isTermSelected(facet.field, value.value),
        }));
    },
    toggleColourSwatch(facet, item) {
      this.toggleTerm(facet.field, item.label);
    },
    facetValues(facet) {
      const values = (facet.data || []).filter((value) => value.count > 0);

      // Curate which values show for facets that have an allowlist (e.g.
      // Category), preserving the allowlist order. Other facets show all.
      const allowlist = nostoFacetValueAllowlists[facet.field];
      if (allowlist && allowlist.length) {
        return allowlist
          .map((name) => values.find((value) => value.value === name))
          .filter(Boolean);
      }

      return values;
    },
    seedTermSelections() {
      const selections = {};
      (this.facets || []).forEach((facet) => {
        if (this.isStatsFacet(facet)) return;
        const selected = (facet.data || [])
          .filter((value) => value.selected)
          .map((value) => value.value);
        if (selected.length) {
          selections[facet.field] = selected;
        }
      });
      this.termSelections = selections;
    },
    isTermSelected(field, value) {
      return (this.termSelections[field] || []).includes(value);
    },
    toggleTerm(field, value) {
      const current = this.termSelections[field]
        ? [...this.termSelections[field]]
        : [];
      const index = current.indexOf(value);

      if (index === -1) {
        current.push(value);
      } else {
        current.splice(index, 1);
      }

      const next = { ...this.termSelections };
      if (current.length) {
        next[field] = current;
      } else {
        delete next[field];
      }
      this.termSelections = next;
    },
    priceBands(facet) {
      return PRICE_BANDS.filter((band) => band.min <= facet.max);
    },
    bandKey(band) {
      return `${band.min}-${band.max}`;
    },
    isBandSelected(field, band) {
      return (this.priceSelections[field] || []).includes(this.bandKey(band));
    },
    togglePriceBand(field, band) {
      const key = this.bandKey(band);
      const current = this.priceSelections[field]
        ? [...this.priceSelections[field]]
        : [];
      const index = current.indexOf(key);

      if (index === -1) {
        current.push(key);
      } else {
        current.splice(index, 1);
      }

      const next = { ...this.priceSelections };
      if (current.length) {
        next[field] = current;
      } else {
        delete next[field];
      }
      this.priceSelections = next;
    },
    formatBand({ min, max }) {
      const minLabel = this.$filters.toCurrency(min, this.currencyCode, 0);
      if (max == null) {
        return `${minLabel}+`;
      }
      const maxLabel = this.$filters.toCurrency(max, this.currencyCode, 0);
      return `${minLabel} - ${maxLabel}`;
    },
    buildFilters() {
      const filters = [];

      Object.entries(this.termSelections).forEach(([field, values]) => {
        if (values.length) {
          filters.push({ field, value: values });
        }
      });

      Object.entries(this.priceSelections).forEach(([field, keys]) => {
        if (!keys.length) return;

        const bands = PRICE_BANDS.filter((band) =>
          keys.includes(this.bandKey(band))
        );
        const min = Math.min(...bands.map((band) => band.min));
        const hasOpenEnded = bands.some((band) => band.max == null);
        const max = hasOpenEnded
          ? null
          : Math.max(...bands.map((band) => band.max));

        const range = { gte: String(min) };
        if (max != null) {
          range.lte = String(max);
        }
        filters.push({ field, range: [range] });
      });

      return filters;
    },
    applyFilter() {
      this.setIsFiltersOpen(false);
      this.$emit("filterChange", cloneDeep(this.buildFilters()));
    },
    handleResetClick() {
      this.termSelections = {};
      this.priceSelections = {};
      this.setIsFiltersOpen(false);
      this.$emit("filterChange", []);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.button-group {
  display: flex;
  gap: 10px;
}

.nosto-filters {
  @extend .containerV2;
}

.nosto-filters__price,
.nosto-filters__terms {
  padding: 4px 0;
}

.list-filter-button {
  @include utility-label(15px);

  width: calc(100% + #{$v-container-padding--mobile * 2}px);
  display: block;
  background: none;
  outline: none;
  border: none;
  text-align: left;
  padding: 10px #{$v-container-padding--mobile}px;
  margin: 0 -#{$v-container-padding--mobile}px;

  @include respond-above(md) {
    width: calc(100% + #{$v-container-padding--desktop * 2}px);
    margin: 0 -#{$v-container-padding--desktop}px;
    padding: 10px #{$v-container-padding--desktop}px;
  }

  &--active {
    background-color: $color-grey-light;
    border-left: 2px solid $color-black;
    padding-left: calc(#{$v-container-padding--mobile}px - 2px);

    @include respond-above(md) {
      padding-left: calc(#{$v-container-padding--desktop}px - 2px);
    }
  }
}
</style>
